import { BrowserQRCodeReader } from "@zxing/browser";
import jsQR from "jsqr";

const uuidRe = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/;
const kvRe = /"(bsdId|id)"\s*:\s*"([0-9a-fA-F-]{36})"/;

export const extractId = (raw: string): string | null => {
    const trimmed = String(raw || "").trim();
    try {
        const asJson = JSON.parse(trimmed);
        const v = asJson?.bsdId || asJson?.id;
        if (typeof v === "string" && uuidRe.test(v)) return v;
    } catch (err) {
        console.log(err)
    }
    const m2 = trimmed.match(kvRe);
    if (m2 && uuidRe.test(m2[2])) return m2[2];
    const m3 = trimmed.match(uuidRe);
    if (m3) return m3[0];
    return null;
};

export const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

export const drawToCanvas = (
    img: HTMLImageElement,
    rotateDeg = 0,
    scale = 1,
    crop?: { x: number; y: number; w: number; h: number }
) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const w = crop ? crop.w : img.width;
    const h = crop ? crop.h : img.height;

    const rad = (rotateDeg * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));

    const outW = Math.max(1, Math.floor((w * cos + h * sin) * scale));
    const outH = Math.max(1, Math.floor((w * sin + h * cos) * scale));

    canvas.width = outW;
    canvas.height = outH;

    const cx = outW / 2;
    const cy = outH / 2;

    const sx = crop ? crop.x : 0;
    const sy = crop ? crop.y : 0;

    ctx.translate(cx, cy);
    ctx.rotate(rad);
    ctx.scale(scale, scale);
    ctx.drawImage(img, sx - w / 2, sy - h / 2, img.width, img.height);

    return canvas;
};

export const enhance = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const { width, height } = canvas;
    const imgData = ctx.getImageData(0, 0, width, height);
    const d = imgData.data;

    let min = 255,
        max = 0;
    for (let i = 0; i < d.length; i += 4) {
        const g = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0;
        min = g < min ? g : min;
        max = g > max ? g : max;
    }
    const range = Math.max(1, max - min);
    const contrast = 1.25;

    for (let i = 0; i < d.length; i += 4) {
        let g = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0;
        g = ((g - min) / range) * 255;
        g = (g - 128) * contrast + 128;
        if (g < 0) g = 0;
        if (g > 255) g = 255;
        d[i] = d[i + 1] = d[i + 2] = g;
    }
    ctx.putImageData(imgData, 0, 0);
    return canvas;
};

export const tryBarcodeDetector = async (canvas: HTMLCanvasElement) => {
    const Detector: any = (window as any).BarcodeDetector;
    if (!Detector) return null;
    let supportsQr = false;
    try {
        const formats = await Detector.getSupportedFormats?.();
        supportsQr = Array.isArray(formats) ? formats.includes("qr_code") : true;
    } catch {
        supportsQr = true;
    }
    if (!supportsQr) return null;

    try {
        const bd = new Detector({ formats: ["qr_code"] });
        const blob = await new Promise<Blob>((resolve) =>
            canvas.toBlob((b) => resolve(b as Blob), "image/png")
        );
        const url = URL.createObjectURL(blob);
        const img = await loadImage(url);
        const detCanvas = document.createElement("canvas");
        detCanvas.width = img.width;
        detCanvas.height = img.height;
        detCanvas.getContext("2d")!.drawImage(img, 0, 0);
        const bitmap = await createImageBitmap(detCanvas);
        const codes = await bd.detect(bitmap as any);
        URL.revokeObjectURL(url);
        if (codes && codes.length > 0) {
            const raw = String(codes[0].rawValue ?? "");
            return raw || null;
        }
    } catch {
        // null
    }
    return null;
};

export const tryJsQR = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const { width, height } = canvas;
    const img = ctx.getImageData(0, 0, width, height);
    const code = jsQR(img.data, width, height, { inversionAttempts: "attemptBoth" });
    return code?.data ?? null;
};

export const decodeWithZXing = async (url: string) => {
    const reader = new BrowserQRCodeReader();
    const result = await reader.decodeFromImageUrl(url);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const text = String(result?.getText?.() ?? result?.text ?? "");
    return text;
};
